import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const goToHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' : 'bg-black/60 backdrop-blur-xl border-b border-white/5'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={goToHome}>
            <img 
              src="/sg-cabs-logo.png" 
              alt="SG Cabs Logo" 
              className="w-16 h-16 object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('services')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              Services
            </button>
            <button onClick={() => scrollToSection('routes')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              Routes
            </button>
            <button onClick={() => scrollToSection('intercity')} data-testid="nav-intercity" className="text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              Intercity
            </button>
            <button onClick={() => scrollToSection('fare-calculator')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              Fares
            </button>
            <button onClick={() => scrollToSection('fleet')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              Fleet
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              Contact
            </button>
          </nav>

          {/* Call Button */}
          <div className="hidden md:flex items-center space-x-4">
            <a href="tel:+917507867648" className="flex items-center space-x-2 text-[#d4af37] hover:text-[#f0d47a] font-bold transition-colors">
              <Phone className="w-5 h-5" />
              <span className="font-semibold">+91 75078 67648</span>
            </a>
            <Button onClick={() => scrollToSection('booking')} className="rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold">
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 text-gray-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <nav className="flex flex-col space-y-4">
              <button onClick={() => scrollToSection('services')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold text-left">
                Services
              </button>
              <button onClick={() => scrollToSection('routes')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold text-left">
                Routes
              </button>
              <button onClick={() => scrollToSection('intercity')} data-testid="nav-intercity-mobile" className="text-[#d4af37] hover:text-[#f0d47a] font-bold text-left">
                Intercity
              </button>
              <button onClick={() => scrollToSection('fare-calculator')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold text-left">
                Fares
              </button>
              <button onClick={() => scrollToSection('fleet')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold text-left">
                Fleet
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-[#d4af37] hover:text-[#f0d47a] font-bold text-left">
                Contact
              </button>
              <a href="tel:+917507867648" className="flex items-center space-x-2 text-[#d4af37] font-semibold">
                <Phone className="w-5 h-5" />
                <span>+91 75078 67648</span>
              </a>
              <Button onClick={() => scrollToSection('booking')} className="rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold w-full">
                Book Now
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
